import './style.css';
import { SITE, APPS, DESKTOP } from './src/content.js';
import { CRTRenderer } from './src/crt/CRTRenderer.js';
import { Scene } from './src/scene/Scene.js';
import { attachInput } from './src/input.js';
import {
  initializeAnalytics,
  trackButtonClick,
  trackAppClick,
  trackDesktopViewed,
  trackAppsViewed,
  trackWindowResize
} from './src/analytics/tracker.js';

initializeAnalytics();

const PIXEL_RATIO_CAP = 2;
const glCanvas = document.getElementById('crt');
const dom = document.getElementById('dom');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Analytics on the real anchors (used by both canvas clicks and keyboard users).
dom.querySelectorAll('a[data-hit]').forEach(a => {
  a.addEventListener('click', () => {
    const id = a.dataset.hit;
    if (id.startsWith('app-')) {
      trackAppClick(id.slice(4), a.dataset.name || a.textContent.trim(), a.href);
    } else {
      trackButtonClick(a.dataset.buttonId || id, a.textContent.trim(), a.href);
    }
  });
});

function fallbackToDom(reason) {
  console.warn('[uxs] CRT renderer unavailable, showing plain DOM:', reason);
  document.documentElement.classList.add('no-webgl');
}

async function boot() {
  const source = document.createElement('canvas');
  let renderer;
  try {
    renderer = new CRTRenderer(glCanvas, source);
  } catch (err) {
    fallbackToDom(err);
    return;
  }

  // Wait for the pixel fonts so canvas text measures correctly (with a timeout).
  await Promise.race([
    Promise.all([document.fonts.load('16px "Press Start 2P"'), document.fonts.load('16px "VT323"')]),
    new Promise(res => setTimeout(res, 2500))
  ]).catch(() => {});

  const scene = new Scene({
    canvas: source,
    site: SITE,
    apps: APPS,
    desktop: DESKTOP,
    reducedMotion,
    onEvent: name => {
      if (name === 'desktop_viewed') trackDesktopViewed();
      if (name === 'apps_viewed') trackAppsViewed();
    }
  });

  const resize = () => {
    const dpr = Math.min(window.devicePixelRatio || 1, PIXEL_RATIO_CAP);
    const W = window.innerWidth;
    const H = window.innerHeight;
    scene.resize(W, H, dpr);
    renderer.allocate();
    // one scanline every ~3 CSS px (same pitch as the old CSS scanlines)
    renderer.params.scanlineCount = Math.round(H / 3);
  };
  resize();
  window.addEventListener('resize', () => {
    resize();
    trackWindowResize(window.innerWidth, window.innerHeight);
  });

  attachInput(glCanvas, scene, {
    anchorFor: id => dom.querySelector(`a[data-hit="${id}"]`)
  });

  // Keyboard users tab through the hidden DOM links; mirror focus on the canvas.
  dom.addEventListener('focusin', e => {
    const id = e.target && e.target.dataset && e.target.dataset.hit;
    if (id) scene.setFocus(id);
  });
  dom.addEventListener('focusout', () => scene.setFocus(null));

  if (reducedMotion) {
    renderer.params.flickerStrength = 0;
  }

  // Optional tweak panel: open the site with ?tweak to tune the shader live.
  if (new URLSearchParams(location.search).has('tweak')) {
    import('https://cdn.jsdelivr.net/npm/tweakpane@4.0.5/dist/tweakpane.min.js')
      .then(({ Pane }) => setupTweakPane(Pane, renderer))
      .catch(err => console.warn('[uxs] tweakpane failed to load', err));
  }

  window.__uxs = { scene, renderer };
  glCanvas.classList.add('on');
  const loop = now => {
    scene.update(now);
    scene.draw(now);
    renderer.render(now * 0.001);
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);
}

function setupTweakPane(Pane, renderer) {
  const pane = new Pane({ title: 'CRT', expanded: true });
  pane.element.style.position = 'fixed';
  pane.element.style.top = '16px';
  pane.element.style.right = '16px';
  pane.element.style.zIndex = '10';
  const p = renderer.params;
  pane.addBinding(p, 'enabled');
  pane.addBinding(p, 'smoothing').on('change', ev => renderer.setSmoothing(ev.value));
  const f1 = pane.addFolder({ title: 'Scanlines' });
  f1.addBinding(p, 'scanlineIntensity', { min: 0, max: 1, step: 0.01 });
  f1.addBinding(p, 'scanlineCount', { min: 50, max: 1200, step: 1 });
  f1.addBinding(p, 'adaptiveIntensity', { min: 0, max: 1, step: 0.01 });
  const f2 = pane.addFolder({ title: 'Color' });
  f2.addBinding(p, 'brightness', { min: 0.6, max: 1.8, step: 0.01 });
  f2.addBinding(p, 'contrast', { min: 0.6, max: 1.8, step: 0.01 });
  f2.addBinding(p, 'saturation', { min: 0, max: 2, step: 0.01 });
  const f3 = pane.addFolder({ title: 'Bloom' });
  f3.addBinding(p, 'bloomIntensity', { min: 0, max: 1.5, step: 0.01 });
  f3.addBinding(p, 'bloomThreshold', { min: 0, max: 1, step: 0.01 });
  f3.addBinding(p, 'rgbShift', { min: 0, max: 1, step: 0.01 });
  const f4 = pane.addFolder({ title: 'Framing' });
  f4.addBinding(p, 'vignetteStrength', { min: 0, max: 2, step: 0.01 });
  f4.addBinding(p, 'curvature', { min: 0, max: 0.5, step: 0.005 });
  f4.addBinding(p, 'flickerStrength', { min: 0, max: 0.15, step: 0.001 });
  pane.addButton({ title: 'Log values' }).on('click', () => console.log(JSON.stringify(p, null, 2)));
}

boot();
